import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { SITE_NAME, LOGO_URL } from '../../constants';
import { useAuth } from '../../hooks/useAuth';
import { usePlan } from '../../hooks/usePlan';
import { useToast } from '../ui/Toast';
import type { BillingCycle, TierId } from './tiersData';
import { PricingHero } from './PricingHero';
import { PricingGrid } from './PricingGrid';
import { PricingComparisonTable } from './PricingComparisonTable';
import { PricingFAQ } from './PricingFAQ';
import { PricingCTA } from './PricingCTA';

const PAGE_TITLE = `Preços — ${SITE_NAME}`;
const META_DESCRIPTION =
  'Preços do Vecta Forms: plano Free grátis para sempre, Pro a partir de R$ 47/mês e Business a partir de R$ 97/mês. Cancele quando quiser. Sem taxas escondidas.';
const PAGE_URL = 'https://oneform.com.br/pricing';
const OG_IMAGE = '/one-form-logo/SVG/favicon-one-form.svg';

export const PricingPage: React.FC = () => {
  // OQ9 Pax: "Mensal" default (usuário vê preço base, anual vira upsell)
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { effectiveTier, upgrade } = usePlan();
  const { showToast } = useToast();

  // Visual hierarchy ativa apenas pra usuários autenticados (anon: CTAs default de registro)
  const currentTier: TierId | undefined = user ? effectiveTier : undefined;

  const handleSelect = async (tier: TierId) => {
    // Anon ou auth ainda carregando: navega pro register com intent preservado.
    // Importante passar handleSelect sempre ao Card (mesmo anon) pra evitar
    // comportamento inconsistente quando user state oscila durante loading.
    if (!user) {
      if (authLoading) return; // ignora click até auth resolver
      navigate(`/auth/register?intent=upgrade&tier=${tier}&cycle=${billingCycle}`);
      return;
    }
    if (tier === effectiveTier) return;
    if (tier === 'free') {
      navigate('/dashboard/settings');
      return;
    }
    if (tier === 'pro' && effectiveTier === 'business') {
      navigate('/dashboard/settings');
      return;
    }
    // Upgrade path — aguarda result + mostra toast de erro inline (sem full reload).
    const result = await upgrade(tier, billingCycle);
    if (result.ok) return;
    const errCode = result.error;
    console.error('Upgrade error:', errCode);
    const msg =
      errCode === 'endpoint_unavailable'
        ? 'Checkout temporariamente indisponível. Tente novamente em instantes.'
        : errCode === 'not_authenticated'
          ? 'Sessão expirada. Faça login e tente novamente.'
          : 'Não foi possível iniciar o checkout. Tente novamente ou acesse Configurações.';
    showToast('error', msg);
  };

  // Direct document.title fallback — react-helmet-async v3 has known issues
  // updating <title> across route changes; this guarantees SEO-critical title
  // lands in the DOM regardless of Helmet's internal state machine.
  useEffect(() => {
    const previous = document.title;
    document.title = PAGE_TITLE;
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <div className="min-h-screen bg-neutral-950 relative overflow-hidden">
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={META_DESCRIPTION} />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={META_DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={META_DESCRIPTION} />
      </Helmet>

      {/* Background blobs (match AuthLayout / DashboardLayout) */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[100px]"
          style={{ animation: 'floatBlob 15s ease-in-out infinite' }}
        />
        <div
          className="absolute bottom-[-20%] left-[-5%] w-[400px] h-[400px] bg-purple-600/10 rounded-full blur-[80px]"
          style={{ animation: 'floatBlob 20s ease-in-out infinite reverse' }}
        />
      </div>

      {/* Top nav */}
      <header className="relative z-10">
        <nav
          aria-label="Navegação principal"
          className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between gap-4"
        >
          <Link to="/" className="flex items-center" aria-label={SITE_NAME}>
            <img src={LOGO_URL} alt={SITE_NAME} className="h-6 sm:h-7 w-auto" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 min-h-[44px] px-3 py-2 rounded-lg text-sm text-neutral-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Link>
        </nav>
      </header>

      <main className="relative z-10 px-4 sm:px-6 pt-8 sm:pt-12 pb-16">
        <PricingHero billingCycle={billingCycle} onCycleChange={setBillingCycle} />
        <PricingGrid
          billingCycle={billingCycle}
          currentTier={currentTier}
          onSelect={handleSelect}
        />
        <PricingComparisonTable />
        <PricingFAQ />
        <PricingCTA />
      </main>

      <footer className="relative z-10 border-t border-neutral-800/50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center text-sm text-neutral-400">
          &copy; {new Date().getFullYear()} {SITE_NAME}. Todos os direitos reservados.
        </div>
      </footer>
    </div>
  );
};
